import { useCallback, useState } from 'react'

import { DrawingParameterTextAnnotation, DrawingType } from '@editor/interfaces/drawing'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { ShapeKeyType } from '@editor/interfaces/shape'
import {
  DrawingsState,
  removeDrawings,
  updateEditorElements,
} from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getCurrentAndDescendantIds, getId } from '@editor/pages/projects/editor/infoPanels/utils'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { isInspectionItem } from '@editor/services/InspectionSheet'
import { isShape } from '@editor/services/Shape'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { toggleHiddenElementIds, toggleSelectedElementIds } from '@editor/store/tool'
import { difference } from 'lodash'

const useEditorElementEventHandlers = (
  groupLayers: Record<string, EditorPanelItem[]>,
  toolPanelItems: Record<string, EditorPanelItem[]>,
) => {
  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const hiddenElementIds = useAppSelector((state) => state.toolUtil.core.hiddenElementIds)
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const drawings = useAppSelector((state) => state.blueprint.drawings.document.drawings)

  // States
  const [collapsedAreas, setCollapsedAreas] = useState<string[]>([])

  /**
   * Get all element IDs in an area (both grouped and ungrouped)
   */
  const getAllAreaElementIds = useCallback(
    (areaId: string) => {
      const unlabeled = toolPanelItems[areaId][0] ? getCurrentAndDescendantIds(toolPanelItems[areaId][0], false) : [] // 0 since there's ever only one unlabeled parent item
      const grouped = groupLayers[areaId].map((group) => getCurrentAndDescendantIds(group, true)).flat()
      return [...unlabeled, ...grouped]
    },
    [toolPanelItems, groupLayers],
  )

  /**
   * Check if all elements in an area are invisible
   */
  const isInvisible = useCallback(
    (areaId: string) => difference(getAllAreaElementIds(areaId), hiddenElementIds).length === 0,
    [getAllAreaElementIds, hiddenElementIds],
  )

  /**
   * Collects the element IDs for a given group or item.
   */
  const collectElementIds = useCallback(
    (areaId: string, id: string) => {
      // Check if it's a group or an item
      const group = groupLayers[areaId].find((g) => getId(g) === id)

      let editorElements: DrawingsState['document']['editorElements']

      // If it's a group, mark all items in the group as checked
      if (group) {
        editorElements = {
          inspectionItems:
            group.children
              ?.filter((item) => isInspectionItem(item.item))
              .map((item) => ({ id: getId(item), order: 0 })) || [],
          polygons:
            group.children
              ?.filter((item) => isShape(item.item, ShapeKeyType.POLYGON))
              .map((item) => ({ id: getId(item), order: 0 })) || [],
          cylinders:
            group.children
              ?.filter((item) => isShape(item.item, ShapeKeyType.CYLINDER))
              .map((item) => ({ id: getId(item), order: 0 })) || [],
        }
      } else {
        editorElements = {
          inspectionItems: inspectionItems
            .filter((item) => id === item.inspection_item_id)
            .map((item) => ({ id: item.inspection_item_id!, order: 0 })),
          polygons: shapes.polygons
            .filter((shape) => id === shape.shape_id)
            .map((shape) => ({ id: shape.shape_id, order: 0 })),
          cylinders: shapes.cylinders
            .filter((shape) => id === shape.shape_id)
            .map((shape) => ({ id: shape.shape_id, order: 0 })),
        }
      }

      return editorElements
    },
    [inspectionItems, shapes, groupLayers],
  )

  /**
   * Get annotations linked to the given inspection item or shape IDs.
   * Provided IDs can be a mix of shape and inspection item IDs.
   *
   * @param ids The IDs of the inspection items or shapes.
   * @return The UUIDs of the linked annotations.
   */
  const getLinkedAnnotations = useCallback(
    (ids: string[]) => {
      const annotations = getDrawingsOfType<DrawingParameterTextAnnotation>(drawings, DrawingType.TextAnnotation)

      return ids
        .map(
          (selectedId) =>
            inspectionItems
              .map((item) => {
                // Since we don't know if the selected ID is a shape ID or an inspection item ID, check both
                if (item.shape_ids.polygons?.includes(selectedId) || item.inspection_item_id === selectedId) {
                  const drawing = annotations.find(({ drawing_parameters: { leadAnchors } }) =>
                    leadAnchors.some((anchor) => anchor.inspectionItemId === item.inspection_item_id),
                  )

                  return drawing?.drawing_parameters.uuid
                }

                return undefined
              })
              .filter(Boolean) as string[],
        )
        .flat()
    },
    [inspectionItems, drawings],
  )

  /**
   * Add additional element IDs related to the given IDs (e.g., annotations linked to inspection items/shapes)
   */
  const addAdditionalElementIds = useCallback(
    (ids: string[]): string[] => getLinkedAnnotations(ids).concat(ids),
    [getLinkedAnnotations],
  )

  /**
   * Handle toggling visibility of all elements in an area
   */
  const handleToggleAreaVisibility = useCallback(
    (areaId: string) => {
      const invisible = !isInvisible(areaId)
      dispatch(toggleHiddenElementIds({ ids: getAllAreaElementIds(areaId), invisible }))
    },
    [isInvisible, dispatch, getAllAreaElementIds],
  )

  /**
   * Handle selecting elements in a layer.
   */
  const handleSelectLayer = useCallback(
    (ids: string[], selected: boolean) =>
      dispatch(toggleSelectedElementIds({ ids: addAdditionalElementIds(ids), selected })),
    [addAdditionalElementIds, dispatch],
  )

  /**
   * Handle toggling visibility of elements in a layer.
   */
  const handleToggleVisibility = useCallback(
    (ids: string[], invisible: boolean) => {
      dispatch(toggleHiddenElementIds({ ids: addAdditionalElementIds(ids), invisible }))
    },
    [addAdditionalElementIds, dispatch],
  )

  /**
   * When an item is checked, set it to the blueprint document.
   * The saving will be handled by useDrawings hook.
   */
  const handleCheckToggle = useCallback(
    (areaId: string, ids: string[], checked: boolean) => {
      const editorElements: DrawingsState['document']['editorElements'] = {
        inspectionItems: [],
        polygons: [],
        cylinders: [],
      }
      ids.forEach((id) => {
        const current = collectElementIds(areaId, id)
        editorElements.inspectionItems.push(...current.inspectionItems)
        editorElements.polygons.push(...current.polygons)
        editorElements.cylinders.push(...current.cylinders)
      })

      // Go through drawings to find linked annotations and remove them if the element is being unchecked
      if (!checked) {
        const linkedIds = getLinkedAnnotations([
          ...editorElements.inspectionItems.map(({ id }) => id),
          ...editorElements.polygons.map(({ id }) => id),
        ])
        dispatch(removeDrawings(linkedIds))
      }

      dispatch(updateEditorElements({ editorElements, checked }))
    },
    [collectElementIds, getLinkedAnnotations, dispatch],
  )

  /**
   * Handle collapsing/expanding an area
   */
  const handleCollapseArea = useCallback(
    (areaId: string) => {
      setCollapsedAreas((prev) => (prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]))
    },
    [setCollapsedAreas],
  )

  return {
    handleSelectLayer,
    handleToggleVisibility,
    handleToggleAreaVisibility,
    handleCheckToggle,
    handleCollapseArea,
    isInvisible,
    collapsedAreas,
  }
}

export default useEditorElementEventHandlers
